import React, { type ReactElement, type ReactNode } from 'react';

import classnames from 'classnames';

import { Button, type ButtonProps } from '../Button/Button';
import { Tooltip, type TooltipProps } from '../Tooltip/Tooltip';

import styles from './FooterActions.module.scss';

export type FooterButtonProps = {
    key?: string | number;
    content: ReactNode;
    tooltip?: Omit<TooltipProps, 'children'>;
} & Omit<ButtonProps, 'children'>;

type FooterActionProps = {
    className?: string;
    withSeparator?: boolean;
    buttons?: FooterButtonProps[];
    children?: ReactNode;
};

export const FooterActions = ({
    className,
    buttons,
    withSeparator,
    children
}: FooterActionProps): ReactElement | null => {
    if (!children && !buttons?.length) {
        return null;
    }
    return (
        <div
            className={classnames(
                styles.footerActions,
                { [styles.withSeparator]: withSeparator },
                className
            )}
        >
            {children}
            {buttons &&
                buttons.map(
                    (
                        {
                            className: footerButtonClassName,
                            content,
                            type,
                            key,
                            tooltip,
                            ...rest
                        }: FooterButtonProps,
                        idx: number
                    ) => {
                        const button = (
                            <Button
                                className={classnames(footerButtonClassName, styles.footerButton)}
                                type={type as never}
                                key={key ?? idx}
                                {...rest}
                            >
                                {content}
                            </Button>
                        );

                        if (!tooltip) {
                            return <React.Fragment key={key ?? idx}>{button}</React.Fragment>;
                        }

                        return (
                            <Tooltip {...tooltip} key={key ?? idx}>
                                {button}
                            </Tooltip>
                        );
                    }
                )}
        </div>
    );
};
